import { Button, Card, Form, Input, DatePicker, Select, Row, Col, Popover, Space } from 'antd';
import DisplayAlert from '../../../components/Commons/Alert';
import GenericTable from '../../../components/Commons/Table/generic-table.component';
import ShowDataLength from '../../../components/Commons/Table/results-length-table.component';
import { useEffect, useState } from 'react';
import { PaginationProps } from 'antd/lib';
import { FilterValue } from 'antd/es/table/interface';
import { useMutation, useQuery } from "@tanstack/react-query";
import ExportBtn from '../../../components/Commons/Export/ExportButton';
import { downloadExcel, formatCurrency } from '../../../services/utils';
import { getAll as loadServices, update as updateServices } from '../../../services/bookingServices.service';
import { getAll as loadBookings, update as updateBookings } from '../../../services/bookings.service';
import { RangePickerProps } from 'antd/lib/date-picker';
import { getAll as loadWorkers, getById as loadWorker } from '../../../services/workers.service';
import { isMobile } from 'react-device-detect';
import FilterButton from '../../../components/Commons/Table/FilterButton';
import dayjs from 'dayjs';
import SearchInput from '../../../components/Commons/Input/SearchInput';

const { Search } = Input;

export default function List(props: any) {

    const defaultOrder = props.type === 'operators'
        ? 'start_date-asc'
        : props.type === 'staff'
        ? 'start-asc'
        : props.type === 'suppliers'
        ? 'start-asc'
        : undefined;

    const [services, setServices] = useState<any>([]);
    const [currentPage, setCurrentPage] = useState<number | undefined>(1);
    const [perPage, setPerPage] = useState<number | undefined>(100);
    const [total, setTotal] = useState<number>(0);
    const [search, setSearch] = useState<string>('');
    const [order, setOrder] = useState<string | undefined>(defaultOrder);
    const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));    
    const [servicesToExport, setServicesToExport] = useState([]);
    const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
    const [wasPaid, setWasPaid] = useState<string>('');
    const [worker, setWorker] = useState<any | undefined>(undefined);
    const [workerId, setWorkerId] = useState<number | string | undefined>(undefined);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [totalValue, setTotalValue] = useState<number>(0);
    const [formSearch] = Form.useForm();
    const [formFilter] = Form.useForm();

    const {
        isLoading: getServicesLoading,
        refetch: refetchData,
        isRefetching: isRefetchingGetServices
    } = useQuery(
        ['loadData', currentPage, search, order, perPage, startDate, endDate, wasPaid, workerId],
        () => {
            if (props.type === 'operators') {
                return loadBookings({
                    page: currentPage, per_page: perPage, was_paid: wasPaid,
                    order, search, start_date: startDate, end_date: endDate,
                    operator_id: Number(workerId), firstService: '1'
                });
            }

            return loadServices({
                page: currentPage, per_page: perPage, was_paid: wasPaid,
                order, search, start_date: startDate, end_date: endDate, onlyWorkerType: props.type,
                staff_id: props.type === 'staff' ? Number(workerId) : undefined,
                supplier_id: props.type === 'suppliers' ? Number(workerId) : undefined,
                service_state: props.type === 'staff' ? 'Fechado' : undefined,
                charge: props.type === 'staff' ? 'driver' : undefined,
            });
        },
        {
            onSuccess: response => {
                setServices(response.data.map((record: any) => {
                    return {
                        key: record.data.id,
                        ...record.data,
                        ...record.relationships
                    }
                }));

                setTotalValue(props.type === 'suppliers'
                    ? response?.meta?.total_value_table
                    : (props.type === 'staff'
                        ? response?.meta?.total_staff
                        : response?.meta?.total_value
                    ));
                setServicesToExport([]);
                setCurrentPage(response.meta.current_page);
                setPerPage(response.meta.per_page);
                setTotal(response.meta.total);
            },
            refetchOnWindowFocus: false
        }
    );

    const { refetch: loadDataToExport } = useQuery(
        ['loadDataToExport'],
        () => {
            if (props.type === 'operators') {
                return loadBookings({
                    was_paid: wasPaid, operator_id: Number(workerId), firstService: '1',
                    order, search, start_date: startDate, end_date: endDate,
                });
            }

            return loadServices({
                was_paid: wasPaid, onlyWorkerType: props.type,
                order, search, start_date: startDate, end_date: endDate,
                staff_id: props.type === 'staff' ? Number(workerId) : undefined,
                supplier_id: props.type === 'suppliers' ? Number(workerId) : undefined,
                service_state: props.type === 'staff' ? 'Fechado' : undefined,
                charge: props.type === 'staff' ? 'driver' : undefined,
            });
        },
        {
            onSuccess: response => setServicesToExport(props.onMapDataToExport(response.data)),
            refetchOnWindowFocus: false,
            enabled: false
        }
    );

    const { data: workers } = useQuery(
        ['loadWorkers'],
        () => loadWorkers({ type: props.type, status: 'active' }),
        { refetchOnWindowFocus: false }
    );

    useQuery(
        ['loadWorker', workerId],
        () => workerId ? loadWorker(Number(workerId), { with_relationship: '0' }) : null,
        {
            onSuccess: response => {
                if (response) {
                    setWorker(response);
                }
            }
        }
    );

    const { mutate: updateBookingWasPaidMutate } = useMutation(updateBookings, {
        onError: () => refetchData()
    });

    const { mutate: updateServiceWasPaidMutate } = useMutation(updateServices, {
        onError: () => refetchData()
    });

    const onSearch = (search_value: any) => {
        setCurrentPage(1);
        setSearch(search_value);
    };

    const onFilter = () => {
        setCurrentPage(1);
        let start_date = formFilter.getFieldValue('filter_start_date');
        let end_date = formFilter.getFieldValue('filter_end_date');

        setStartDate(start_date ? start_date.format('YYYY-MM-DD') : '');
        setEndDate(end_date ? end_date.format('YYYY-MM-DD') : '');
        setWasPaid(formFilter.getFieldValue('filter_was_paid'));
        setWorkerId(formFilter.getFieldValue(props.workerFilter));
    };

    const onClearFilters = () => {
        setCurrentPage(1);
        setStartDate('');
        setEndDate('');
        setWasPaid('');
        setWorkerId('')
    };

    const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
        let endDate = formFilter.getFieldValue('filter_end_date');

        if (endDate) {
            return current > endDate;
        }

        return false;
    };

    const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
        let startDate = formFilter.getFieldValue('filter_start_date');

        if (startDate) {
            return current < startDate;
        }

        return false;
    };

    const handleTableChange = (
        pagination: PaginationProps,
        filters: Record<string, FilterValue | null>,
        sorter: any
    ) => {
        if (pagination.current != currentPage) {
            setCurrentPage(pagination.current);
        }

        if (pagination.pageSize != perPage) {
            setPerPage(pagination.pageSize);
            setCurrentPage(1);
        }

        if (sorter.order) {
            let orderBy = `${sorter.field}-${sorter.order.substr(0, sorter.order.length - 3)}`;

            if (order != orderBy) {
                setOrder(orderBy);
            }
        }
    };

    const onChangeWasPaid = (checked: boolean, id: number) => {
        const obj = {
            values: { 'was_paid': checked }, id
        };

        if (props.type === 'operators') {
            updateBookingWasPaidMutate(obj);
        } else {
            updateServiceWasPaidMutate(obj);
        }
    };

    const exportData = () => downloadExcel(servicesToExport, `Controle-${props.label}`);

    const handleOpenFiltersChange = (open: boolean) => {
        setOpenFilters(open);
    };

    const filters = (
        <Form
            form={formFilter}
            name="filter_services"
            layout="vertical"
            className="form_filter_horizontal"
            autoComplete="off"
            style={isMobile ? {} : { width: 300 }}>
            <Row>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label={props.label} name={props.workerFilter} className="item_filter">
                        <Select placeholder="Todos" size="large" value={workerId} allowClear>
                            <Select.Option key={0} value={''}>Todos</Select.Option>
                            {workers?.data?.map((item: any) => (
                                <Select.Option key={item.data.id} value={item.data.id}>{item.data.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item name="filter_start_date" label="Data início" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" format={"DD/MM/YYYY"}
                            style={{ width: '100%' }} disabledDate={disabledStartDate} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item name="filter_end_date" label="Data fim" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" format={"DD/MM/YYYY"}
                            style={{ width: '100%' }} disabledDate={disabledEndDate} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Pagamento" name="filter_was_paid" className="item_filter">
                        <Select placeholder="Todos" value={wasPaid} style={isMobile ? {} : { width: '100%' }} size="large"
                            options={[
                                { value: '', label: 'Todos' },
                                { value: '1', label: 'Pago' },
                                { value: '0', label: 'Não pago' }
                            ]} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Button onClick={onFilter} size="large">Filtrar</Button>

                    {(startDate || endDate || wasPaid || workerId)
                        && <Button type="link" onClick={onClearFilters} size="large">Limpar</Button>
                    }
                </Col>
            </Row>
        </Form>
    );

    useEffect(() => {
        formFilter.setFieldsValue({
            filter_start_date: '',
            filter_end_date: '',
            filter_was_paid: '',
            [props.workerFilter]: ''
        });
    }, []);

    useEffect(() => {
        if (!startDate) {
            formFilter.setFieldValue('filter_start_date', '');
        }

        if (!endDate) {
            formFilter.setFieldValue('filter_end_date', '');
        }

        formFilter.setFieldValue('filter_was_paid', wasPaid);

        formFilter.setFieldValue(props.workerFilter, workerId);
    }, [startDate, endDate, wasPaid, workerId]);

    useEffect(() => {
        if (servicesToExport.length) {
            exportData();
        }

        setExportIsLoading(false);
    }, [servicesToExport]);

    useEffect(() => {
        if (props.changeWasPaid) {
            onChangeWasPaid(props.changeWasPaid.value, props.changeWasPaid.id);
        }
    }, [props.changeWasPaid]);

    return (
        <>
            <DisplayAlert />
            <Card bodyStyle={{ padding: 8 }}>
                <div>
                    <Row className="search_bar">
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                            <SearchInput
                                form={formSearch}
                                name="search_services"
                                buttonText="Pesquisar"
                                onSearch={onSearch}
                                placeholder={`Pesquisa por nome`}
                            />
                        </Col>
                    </Row>
                </div>

                <div className="table">
                    <GenericTable
                        columns={props.columns}
                        dataSource={services}
                        pagination={{
                            current: currentPage,
                            pageSize: perPage,
                            showSizeChanger: true,
                            total: total
                        }}
                        loading={getServicesLoading || isRefetchingGetServices}
                        totalChildren={
                            <div className="zone_result_size_n_export" style={isMobile ? { marginTop: 10 } : {}}>
                                <ShowDataLength size={total} className="show_result_size" />

                                <Space direction={isMobile ? 'vertical' : 'horizontal'} className="show_result_size">
                                    <Form.Item label="Valor total do período" className='item_filter'
                                        style={isMobile ? { width: 100 } : { width: 230 }}>
                                        <Input placeholder='Valor total' readOnly value={formatCurrency(totalValue)} />
                                    </Form.Item>

                                    <FilterButton filters={filters} />

                                    <ExportBtn disabled={!total} loading={exportIsLoading}
                                        onClick={() => {
                                            if (servicesToExport.length) {
                                                exportData();
                                            } else {
                                                setExportIsLoading(true);
                                                loadDataToExport();
                                            }
                                        }} />
                                </Space>
                            </div>
                        }
                        empty={{
                            createTheFirst: true,
                            textOne: !services.length && !search && !startDate && !endDate
                                ? 'Nenhum dado a ser tratado.'
                                : 'Não há resultados para a combinação de filtros selecionada.',
                            textTwo: !services.length && !search && !startDate && !endDate
                                ? ''
                                : 'Tente novamente com diferentes valores de filtro.',
                            link: '',
                            createText: ''
                        }}
                        handleTableChange={handleTableChange}
                        scroll={isMobile ? { x: 800 } : undefined}
                    />
                </div>
            </Card>
        </>
    );
}